import { amDateShema, amDayShema, amMobilityDateRangeSchema } from 'src/common-services/types/alphamap/generic-types'
import { amGeometryPolygonSchema } from 'src/common-services/types/alphamap/geojson-types'
import { amLocationShema } from 'src/common-services/types/alphamap/locations-types'
import { amCensusBlockShema } from 'src/common-services/types/alphamap/shapes-types'
import { YYYYMMDDDateString } from 'src/common-services/types/custom-footfall-types'
import z from 'zod'

export type TrendsDateRange = {
  start: YYYYMMDDDateString
  end: YYYYMMDDDateString
}

export enum amMobilityProvidersEnum {
  alphamap = 'alphamap',
  advan = 'advan',
  olvin = 'olvin',
  unacast_ml = 'unacast', // We need to keep this as is since alphamap_providers/alphamap_locations_providers table users 'unacast' as the value
  unacast_vdp = 'unacast_vdp',
}

export const visitorOriginType = z.enum(['home', 'work'])
export type VisitorOriginType = z.infer<typeof visitorOriginType>

export const boundaryType = z.enum(['zipcode', 'cbg', 'h3'])
export type BoundaryType = z.infer<typeof boundaryType>

export const amMobilityProvidersSchema = z.nativeEnum(amMobilityProvidersEnum)

export type AMMobilityLocationProvider = z.infer<typeof amMobilityProvidersSchema>

export enum amMobilityFrequencyEnum {
  monthly = 'monthly',
  weekly = 'weekly',
  daily = 'daily',
}

export const amMobilityFrequencySchema = z.nativeEnum(amMobilityFrequencyEnum)

export type AMMobilityFrequency = z.infer<typeof amMobilityFrequencySchema>

export enum amMobilityDataProviderEnum {
  Advan = 'advan',
  Unacast = 'unacast',
  PassBy = 'pass_by',
}

export const amMobilityDataProviderSchema = z.nativeEnum(amMobilityDataProviderEnum)

export type AMMobilityDataProvider = z.infer<typeof amMobilityDataProviderSchema>

export const amMobilityLocationVisitsRequestSchema = z.object({
  id: z.string(),
  provider: amMobilityProvidersSchema.optional(),
  frequency: amMobilityFrequencySchema.optional(),
  from: amDateShema.optional(),
  to: amDateShema.optional(),
})

export type AMMobilityLocationVisitsRequest = z.infer<typeof amMobilityLocationVisitsRequestSchema>

export const amMobilityLocationRawSchema = z.object({
  id: z.string(),
  start: amDateShema,
  monthlyVisits: z.number(),
  monthlyVisitorsRatio: z.number(),
  adjustmentRatio: z.number(),
  dailyVisits: z.array(z.number()).nullable(),
})

export type AMMobilityLocationRaw = z.infer<typeof amMobilityLocationRawSchema>

export const amDateValueSchema = z.object({
  date: amDateShema,
  value: z.number(),
})

export type AMDateValue = z.infer<typeof amDateValueSchema>

export const amMobilityLocationVisitorsRequestSchema = z.object({
  id: z.string(),
  provider: amMobilityProvidersSchema.optional(),
  frequency: amMobilityFrequencySchema.optional(),
  from: amDateShema.optional(),
  to: amDateShema.optional(),
})

export type AMMobilityLocationVisitorsRequest = z.infer<typeof amMobilityLocationVisitorsRequestSchema>

export const AMMMobilityTrendsDataSchema = z.object({
  [amMobilityProvidersEnum.advan]: z.array(amDateValueSchema),
  [amMobilityProvidersEnum.alphamap]: z.array(amDateValueSchema),
  [amMobilityProvidersEnum.olvin]: z.array(amDateValueSchema),
  [amMobilityProvidersEnum.unacast_ml]: z.array(amDateValueSchema),
  [amMobilityProvidersEnum.unacast_vdp]: z.array(amDateValueSchema),
})
export type AMMobilityTrendsData = z.infer<typeof AMMMobilityTrendsDataSchema>

export const amMobilityLocationAvailableProvidersRequestSchema = z.object({
  id: z.string(),
})

export type AMMobilityLocationAvailableRequestProviders = z.infer<
  typeof amMobilityLocationAvailableProvidersRequestSchema
>

export const amMobilityLocationProviderIdRequestSchema = z.object({
  id: z.string(),
  provider: amMobilityProvidersSchema,
})

export type AMMobilityLocationProviderIdRequest = z.infer<typeof amMobilityLocationProviderIdRequestSchema>

export const amMobilityLocationProviderIdsRequestSchema = z.object({
  ids: z.array(z.string()),
  provider: amMobilityProvidersSchema,
})

export type AMMobilityLocationProviderIdsRequest = z.infer<typeof amMobilityLocationProviderIdsRequestSchema>

export const amMobilityLocationVisitsByHourRequestSchema = z.object({
  id: z.string(),
  provider: amMobilityDataProviderSchema.optional(),
  dateRange: amMobilityDateRangeSchema,
})

export type AMMobilityLocationVisitsByHourRequest = z.infer<typeof amMobilityLocationVisitsByHourRequestSchema>

export const amHourValueSchema = z.object({
  hour: z.number(),
  value: z.number(),
})

export type AMHourValue = z.infer<typeof amHourValueSchema>

export const amMobilityByHourLocationRawSchema = z.object({
  id: z.string(),
  start: amDateShema,
  visitsByHour: z.array(z.number()),
})

export type AMMobilityByHourLocationRaw = z.infer<typeof amMobilityByHourLocationRawSchema>

export const amMobilityLocationVisitsByDayRequestSchema = z.object({
  id: z.string(),
  provider: amMobilityDataProviderSchema.optional(),
  dateRange: amMobilityDateRangeSchema,
})

export type AMMobilityLocationVisitsByDayRequest = z.infer<typeof amMobilityLocationVisitsByDayRequestSchema>

export const amDayValueSchema = z.object({
  day: amDayShema,
  value: z.number(),
})

export type AMDayValue = z.infer<typeof amDayValueSchema>

export const amMobilityByDayLocationRawSchema = z.object({
  id: z.string(),
  start: amDateShema,
  visitsByDay: z.record(amDayShema, z.number()),
})

export type AMMobilityByDayLocationRaw = z.infer<typeof amMobilityByDayLocationRawSchema>

export const amMobilityLocationVisitorsByHomeRequestSchema = z.object({
  id: z.string(),
  date: amDateShema,
  type: boundaryType.default('zipcode').optional(),
})

export type AMMobilityLocationVisitorsByHomeRequest = z.infer<typeof amMobilityLocationVisitorsByHomeRequestSchema>

export const amCensusBlockValueSchema = z.object({
  id: amCensusBlockShema.shape.id,
  value: z.number(),
})

export type AMCensusBlockValue = z.infer<typeof amCensusBlockValueSchema>

export const amMobilityByHomeLocationRawSchema = z.object({
  id: z.string(),
  start: amDateShema,
  visitorsByHome: z.record(amCensusBlockShema.shape.id, z.number()),
})

export type AMMobilityByHomeLocationRaw = z.infer<typeof amMobilityByHomeLocationRawSchema>

export const amMobilityLocationVisitsByHomeRequestSchema = z.object({
  id: z.string(),
  date: amDateShema,
  type: boundaryType.default('zipcode').optional(),
  visitorOrigin: visitorOriginType.default('home').optional(),
})

export type AMMobilityLocationVisitsByHomeRequest = z.infer<typeof amMobilityLocationVisitsByHomeRequestSchema>

export enum amMobilityAggregatedMethodEnum {
  avg = 'avg',
  total = 'total',
}

export const amMobilityAggregatedMethodShema = z.nativeEnum(amMobilityAggregatedMethodEnum)

export type AMMobilityAggregatedMethod = z.infer<typeof amMobilityAggregatedMethodShema>

export const amMobilityAggregatedVisitsRequestShema = z.object({
  ids: z.array(z.string()),
  method: amMobilityAggregatedMethodShema.optional(),
  frequency: amMobilityFrequencySchema.optional(),
  from: amDateShema.optional(),
  to: amDateShema.optional(),
})

export type AMMobilityAggregatedVisitsRequest = z.infer<typeof amMobilityAggregatedVisitsRequestShema>

export const amMobilityAggregatedRawESchema = z.object({
  start: amDateShema,
  monthlyVisits: z.number(),
  monthlyVisitorsRatio: z.number(),
  adjustmentRatio: z.number(),
  dailyVisits: z.array(z.array(z.number())),
})

export type AMMobilityAggregatedRawE = z.infer<typeof amMobilityAggregatedRawESchema>

export const amMobilityAggregatedRawSchema = z.object({
  start: amDateShema,
  monthlyVisits: z.number(),
  monthlyVisitorsRatio: z.number(),
  adjustmentRatio: z.number(),
  dailyVisits: z.array(z.number()),
})

export type AMMobilityAggregatedRaw = z.infer<typeof amMobilityAggregatedRawSchema>

export const amMobilityPolygonVisitsRequestSchema = z.object({
  polygon: amGeometryPolygonSchema,
  method: amMobilityAggregatedMethodShema.optional(),
  frequency: amMobilityFrequencySchema.optional(),
  from: amDateShema.optional(),
  to: amDateShema.optional(),
})

export type AMMobilityPolygonVisitsRequest = z.infer<typeof amMobilityPolygonVisitsRequestSchema>

export const amMobilityAggregatedVisitorsRequestShema = z.object({
  ids: z.array(z.string()),
  method: amMobilityAggregatedMethodShema.optional(),
  frequency: amMobilityFrequencySchema.optional(),
  from: amDateShema.optional(),
  to: amDateShema.optional(),
})

export type AMMobilityAggregatedVisitorsRequest = z.infer<typeof amMobilityAggregatedVisitorsRequestShema>

export const amMobilityPolygonVisitorsRequestSchema = z.object({
  polygon: amGeometryPolygonSchema,
  method: amMobilityAggregatedMethodShema.optional(),
  frequency: amMobilityFrequencySchema.optional(),
  from: amDateShema.optional(),
  to: amDateShema.optional(),
})

export type AMMobilityPolygonVisitorsRequest = z.infer<typeof amMobilityPolygonVisitorsRequestSchema>

export const amMobilityPolygonTopLocationsRequestSchema = z.object({
  polygon: amGeometryPolygonSchema,
  limit: z.number().min(1).max(1000).optional(),
  page: z.number().min(1).optional(),
})

export type AMMobilityPolygonTopLocationsRequest = z.infer<typeof amMobilityPolygonTopLocationsRequestSchema>

export const amMobilityPolygonTopLocationsVisitsRequestSchema = z.object({
  locationIds: z.string().array(),
})

export type AMMobilityPolygonTopLocationsVisitsRequest = z.infer<
  typeof amMobilityPolygonTopLocationsVisitsRequestSchema
>

export const amMobilityPolygonTopLocationsMatthewsRequestSchema = z.object({
  polygon: amGeometryPolygonSchema,
  limit: z.number().min(1).max(1000).optional(),
  page: z.number().min(1).optional(),
})

export type AMMobilityPolygonTopLocationsMatthewsRequest = z.infer<
  typeof amMobilityPolygonTopLocationsMatthewsRequestSchema
>

export const amMobilityPolygonTopLocationSchema = amLocationShema.extend({
  monthlyVisits: z.number(),
  logoS3Key: z.string(),
})

export type AMMobilityPolygonTopLocation = z.infer<typeof amMobilityPolygonTopLocationSchema>

export const amMobilityPolygonVisitsByHourRequestSchema = z.object({
  polygon: amGeometryPolygonSchema,
  method: amMobilityAggregatedMethodShema.optional(),
  date: amDateShema,
})

export type AMMobilityPolygonVisitsByHourRequest = z.infer<typeof amMobilityPolygonVisitsByHourRequestSchema>

export const amMobilityAggregatedVisitsByHourRequestSchema = z.object({
  ids: z.array(z.string()),
  method: amMobilityAggregatedMethodShema.optional(),
  date: amDateShema,
})

export type AMMobilityAggregatedVisitsByHourRequest = z.infer<typeof amMobilityAggregatedVisitsByHourRequestSchema>

export const amMobilityByHourAggregatedRawSchema = z.object({
  start: amDateShema,
  visitsByHour: z.array(z.array(z.number())),
})

export type AMMobilityByHourAggregatedRaw = z.infer<typeof amMobilityByHourAggregatedRawSchema>

export const amMobilityAggregatedVisitsByDayRequestSchema = z.object({
  ids: z.array(z.string()),
  method: amMobilityAggregatedMethodShema.optional(),
  date: amDateShema,
})

export type AMMobilityAggregatedVisitsByDayRequest = z.infer<typeof amMobilityAggregatedVisitsByDayRequestSchema>

export const amMobilityByDayAggregatedRawSchema = z.object({
  start: amDateShema,
  visitsByDay: z.array(z.record(amDayShema, z.number())),
})

export type AMMobilityByDayAggregatedRaw = z.infer<typeof amMobilityByDayAggregatedRawSchema>

export const amMobilityPolygonVisitsByDayRequestSchema = z.object({
  polygon: amGeometryPolygonSchema,
  method: amMobilityAggregatedMethodShema.optional(),
  date: amDateShema,
})

export type AMMobilityPolygonVisitsByDayRequest = z.infer<typeof amMobilityPolygonVisitsByDayRequestSchema>

export enum amMobilityRankScopeEnum {
  city = 'city',
  state = 'state',
  country = 'country',
}

export const amMobilityRankScopeSchema = z.nativeEnum(amMobilityRankScopeEnum)

export type AMMobilityRankScope = z.infer<typeof amMobilityRankScopeSchema>

export const amMobilityLocationRankByBrandRequestSchema = z.object({
  id: z.string(),
  provider: amMobilityProvidersSchema.optional(),
  date: amDateShema.optional(),
  scope: amMobilityRankScopeSchema.optional(),
  limit: z.number().min(1).optional(),
  offset: z.number().min(0).optional(),
})

export type AMMobilityLocationRankByBrandRequest = z.infer<typeof amMobilityLocationRankByBrandRequestSchema>

export const amMobilityLocationRankBrandRequestSchema = z.object({
  brandId: z.string(),
  provider: amMobilityProvidersSchema.optional(),
  date: amDateShema.optional(),
})
export type AMMobilityLocationRankBrandRequest = z.infer<typeof amMobilityLocationRankBrandRequestSchema>

export const amMobilityLocationRankListSchema = amLocationShema.extend({
  rank: z.number(),
  visits: z.number(),
  percentile: z.number(),
  totalLocations: z.number(),
})

export type AMMobilityLocationRankList = z.infer<typeof amMobilityLocationRankListSchema>

export const amLocationRankByBrandListResponseSchema = z.object({
  data: z.array(amMobilityLocationRankListSchema),
  total: z.number(),
})
export type AMLocationRankByBrandListResponse = z.infer<typeof amLocationRankByBrandListResponseSchema>

export const amMobilityLocationRankPositionShema = z.object({
  rank: z.number().nullable(),
  percentage: z.number().nullable(),
  total: z.number(),
  visits: z.number(),
})

export type AMMobilityLocationRankPosition = z.infer<typeof amMobilityLocationRankPositionShema>

export const amMobilityLocationRankPositionWithIdShema = z
  .object({
    id: z.string(),
  })
  .merge(amMobilityLocationRankPositionShema)
export type AMMobilityLocationRankPositionWithId = z.infer<typeof amMobilityLocationRankPositionWithIdShema>

export const amMobilityLocationRankPositionRawShema = z.object({
  id: z.string(),
  rank: z.number(),
  visits: z.number(),
  total: z.number(),
  percentage: z.number().nullable(),
})

export type AMMobilityLocationRankPositionRaw = z.infer<typeof amMobilityLocationRankPositionRawShema>

export const amMobilityLocationRankByBrandHistogramRequestSchema = amMobilityLocationRankByBrandRequestSchema.extend({
  buckets: z.number().min(2).max(1000).optional(),
})

export type AMMobilityLocationRankByBrandHistogramRequest = z.infer<
  typeof amMobilityLocationRankByBrandHistogramRequestSchema
>

export const amMobilityLocationRankHistogramShema = z.object({
  bucket: z.number(),
  min: z.number(),
  max: z.number(),
  count: z.number(),
})

export type AMMobilityLocationRankHistogram = z.infer<typeof amMobilityLocationRankHistogramShema>

export const amMobilityLocationRankByBrandChartRequestSchema = amMobilityLocationRankByBrandRequestSchema.extend({
  buckets: z.number().min(2).max(1000).optional(),
})

export type AMMobilityLocationRankByBrandChartRequest = z.infer<typeof amMobilityLocationRankByBrandChartRequestSchema>

export const amMobilityLocationRankChartShema = z.object({
  bucket: z.number(),
  count: z.number(),
})

export type AMMobilityLocationRankChart = z.infer<typeof amMobilityLocationRankChartShema>

export const amMobilityLocationDwellTimeRequestSchema = z.object({
  id: z.string(),
  dateRange: amMobilityDateRangeSchema,
})
export type AMMobilityLocationDwellTimeRequest = z.infer<typeof amMobilityLocationDwellTimeRequestSchema>

export const amMobilityAdvanLocationDwellTimeSchema = z.object({
  median: z.number(),
  dwellTimes: z.object({
    '<5': z.number(),
    '5-20': z.number(),
    '21-60': z.number(),
    '61-240': z.number(),
    '>240': z.number(),
  }),
})
export type AMMobilityAdvanLocationDwellTime = z.infer<typeof amMobilityAdvanLocationDwellTimeSchema>

export const amMobilityUnacastLocationDwellTimeSchema = z.object({
  median: z.number(),
  dwellTimes: z
    .object({
      visits: z.number(),
      avgVisitsLength: z.number(),
    })
    .array(),
})
export type AMMobilityUnacastLocationDwellTime = z.infer<typeof amMobilityUnacastLocationDwellTimeSchema>

export const amMobilityLocationRankByBrandOverTimeRequestSchema = z.object({
  id: z.string(),
  provider: amMobilityProvidersSchema.optional(),
  from: amDateShema.optional(),
  to: amDateShema.optional(),
  scope: amMobilityRankScopeSchema.optional(),
})

export type AMMobilityLocationRankByBrandOverTimeRequest = z.infer<
  typeof amMobilityLocationRankByBrandOverTimeRequestSchema
>

export const amMobilityLocationRankByNaicsOverTimeRequestSchema = z.object({
  id: z.string(),
  provider: amMobilityProvidersSchema.optional(),
  from: amDateShema.optional(),
  to: amDateShema.optional(),
  scope: amMobilityRankScopeSchema.optional(),
  naics: z.array(z.string()),
})

export type AMMobilityLocationRankByNaicsOverTimeRequest = z.infer<
  typeof amMobilityLocationRankByNaicsOverTimeRequestSchema
>

export const amMobilityLocationRankByNaicsRequestSchema = z.object({
  id: z.string(),
  provider: amMobilityProvidersSchema.optional(),
  date: amDateShema.optional(),
  scope: amMobilityRankScopeSchema.optional(),
  naics: z.array(z.string()),
})

export type AMMobilityLocationRankByNaicsRequest = z.infer<typeof amMobilityLocationRankByNaicsRequestSchema>

export const amMobilityLocationRankByNaicsChartRequestSchema = amMobilityLocationRankByNaicsRequestSchema.extend({
  buckets: z.number().min(2).max(1000).optional(),
})

export type AMMobilityLocationRankByNaicsChartRequest = z.infer<typeof amMobilityLocationRankByNaicsChartRequestSchema>

export const amMobilityLocationRankByNaicsListRequestSchema = amMobilityLocationRankByNaicsRequestSchema.extend({
  limit: z.number().min(1),
  offset: z.number().min(0),
})

export type AMMobilityLocationRankByNaicsListRequest = z.infer<typeof amMobilityLocationRankByNaicsListRequestSchema>

// Shopping Center locations
export const amMobilityShoppingCenterLocationsRequestSchema = z.object({
  id: z.string(),
  date: amDateShema,
})
export type AMMobilityShoppingCenterLocationsRequest = z.infer<typeof amMobilityShoppingCenterLocationsRequestSchema>

export const amMobilityShoppingCenterLocationSchema = z.object({
  id: z.string(),
  name: z.string(),
  lat: z.number(),
  lng: z.number(),
  logoUrl: z.string().optional(),
  avgMonthlyVisits: z.number().nullable(),
  localPercentile: z.number().nullable(),
  localRank: z.number().nullable(),
  localTotal: z.number().nullable(),
  statePercentile: z.number().nullable(),
  stateRank: z.number().nullable(),
  stateTotal: z.number().nullable(),
  nationalPercentile: z.number().nullable(),
  nationalRank: z.number().nullable(),
  nationalTotal: z.number().nullable(),
  naicsCodes: z.array(z.string()),
  brandId: z.string().optional(),
})
export type AMMobilityShoppingCenterLocations = z.infer<typeof amMobilityShoppingCenterLocationSchema>

export const amMobilityLocationVisitsByHomeDateRangeRequestSchema = z.object({
  id: z.string(),
  type: boundaryType.default('zipcode').optional(),
  startDate: z.string(),
  endDate: z.string(),
  visitorOrigin: visitorOriginType.default('home').optional(),
  percentageFilter: z.number().min(0).max(1).default(1).optional(),
})
export type AMMobilityLocationVisitsByHomeDateRangeRequest = z.infer<
  typeof amMobilityLocationVisitsByHomeDateRangeRequestSchema
>

export const amMobilityLocationVisitsByHomeDateRangeResponseSchema = z.array(
  z.object({
    date: z.string(),
    visitorsByHome: z.array(amCensusBlockValueSchema),
  })
)
export type AMMobilityLocationVisitsByHomeDateRangeRespense = z.infer<
  typeof amMobilityLocationVisitsByHomeDateRangeResponseSchema
>

export const amMobilityPolygonAllRankRequestSchema = amMobilityPolygonTopLocationsRequestSchema
export type AMMobilityPolygonAllRankRequest = z.infer<typeof amMobilityPolygonAllRankRequestSchema>

export const amMobilityPolygonAllRankResponseSchema = z
  .object({
    rank: z.object({
      city: amMobilityLocationRankPositionShema,
      state: amMobilityLocationRankPositionShema,
      country: amMobilityLocationRankPositionShema,
    }),
  })
  .merge(amMobilityPolygonTopLocationSchema)
export type AMMobilityPolygonAllRankResponse = z.infer<typeof amMobilityPolygonAllRankResponseSchema>

const ADDRESS_MAX_LENGTH = 400 as const
const CITY_MAX_LENGTH = 100 as const
const STATE_MAX_LENGTH = 50 as const
const ZIP_MAX_LENGTH = 20 as const

function maxStrLen(len: number) {
  const length = z.number().min(1).parse(len)
  return z.string().trim().max(length, `Should not be longer than or equal to ${length} characters`)
}
const amAreaVisitAddressSchema = z.object({
  address: maxStrLen(ADDRESS_MAX_LENGTH),
  city: maxStrLen(CITY_MAX_LENGTH),
  state: maxStrLen(STATE_MAX_LENGTH),
  zip: maxStrLen(ZIP_MAX_LENGTH),
})
export const amAverageVisitsSchema = amAreaVisitAddressSchema.extend({
  startDate: amDateShema,
  endDate: amDateShema,
})
export type amAverageVisitsRequest = z.infer<typeof amAverageVisitsSchema>

export const amAverageVisitsResultSchemaa = amAreaVisitAddressSchema.extend({
  avgVisits: z.number().nullable(),
})
export type amAverageVisitsResults = z.infer<typeof amAverageVisitsResultSchemaa>

export const amMobilityBrandVisitsRequestSchema = z.object({
  id: z.string(),
  from: amDateShema.optional(),
  to: amDateShema.optional(),
})
export type AMMobilityBrandVisitsRequest = z.infer<typeof amMobilityBrandVisitsRequestSchema>

export const amMobilityBrandVisitsSchema = z.object({
  date: amDateShema,
  visits: z.number(),
  totalLocations: z.number(),
})
export type AMMobilityBrandVisits = z.infer<typeof amMobilityBrandVisitsSchema>

export const amMobilityBrandDayOfWeekVisitsRequestSchema = z.object({
  id: z.string(),
  date: amDateShema,
})
export type AMMobilityBrandDayOfWeekVisitsRequest = z.infer<typeof amMobilityBrandDayOfWeekVisitsRequestSchema>

export const amMobilityBrandDayOfWeekVisitsSchema = z.object({
  totalLocations: z.number(),
  visits: z.array(amDayValueSchema),
})
export type AMMobilityBrandDayOfWeekVisits = z.infer<typeof amMobilityBrandDayOfWeekVisitsSchema>
